import type { ReactNode } from "react"
import {
	IconChevronDownOutline18 as ChevronDownIcon,
	IconShareUpRightOutline18 as ExternalLink,
} from "nucleo-ui-outline-18"
import { useWindowSize } from "react-use"
import resolveConfig from "tailwindcss/resolveConfig"

import { cn } from "@productlane/lib/src/browser"
import { Button, IconButton, Popover, Tooltip } from "@productlane/ui"

import { RoleCheck } from "@/components/shared/role-check"
import { analytics } from "@/lib/analytics"
import { useZeroWorkspace } from "@/lib/zero"
import tailwindConfig from "../../../../tailwind.config"

interface PublishRoadmapPopoverContentProps {
	name: string
	content: ReactNode
	url: string
	isPublished: boolean
}

const fullConfig = resolveConfig(tailwindConfig)

export const PublishPortalPageButton = ({
	name,
	content,
	url,
	isPublished,
}: PublishRoadmapPopoverContentProps) => {
	const workspace = useZeroWorkspace()

	const textConfig = {
		changelog: {
			publish: "Publish Changelog",
			published: "Changelog settings",
			tooltip: "Open Changelog",
		},
		roadmap: {
			publish: "Publish Feature requests",
			published: "Feature requests settings",
			tooltip: "Open Feature requests",
		},
		"support-portal": {
			publish: "Publish Portal",
			published: "Portal settings",
			tooltip: "Open Support Portal",
		},
		docs: {
			publish: "Publish Docs",
			published: "Docs settings",
			tooltip: "Open article",
		},
	}

	const { width } = useWindowSize()
	const md = fullConfig.theme.screens.md

	const nameConfig = textConfig[name as keyof typeof textConfig]

	const label = isPublished ? nameConfig.published : nameConfig.publish

	return (
		<RoleCheck not={["VIEWER"]}>
			<div
				className={cn(
					"flex",
					isPublished &&
						"rounded-full shadow-sm ring-1 ring-black/15 dark:shadow-black dark:ring-white/15",
				)}
			>
				<Popover
					className="w-full rounded-2xl sm:w-[30rem]"
					content={content}
					align="end"
					side="bottom"
				>
					<Button
						Icon={ChevronDownIcon}
						iconClassName="h-3 w-3"
						// Publishing the docs is the step the whole import leads to, so it
						// carries the weight until it is done.
						variant={name === "docs" && !isPublished ? "default" : "outline"}
						className={cn(
							"pr-[11px]",
							isPublished && "rounded-r-none py-0.5 pr-3 shadow-none ring-0",
						)}
						onClick={() => {
							analytics.track(`open-${name.toLocaleLowerCase()}-popover`)
						}}
					>
						{width <= parseInt(md) ? label.split(" ")[0] : label}
					</Button>
				</Popover>

				{isPublished && (
					<>
						<div className="my-1.5 border-l border-gray-300 dark:border-gray-600" />

						<Tooltip label={nameConfig.tooltip}>
							<IconButton
								disabled={!workspace}
								className="w-11 rounded-l-none shadow-none ring-0"
								variant="outline"
								onClick={() => {
									if (!workspace) {
										return
									}
									window.open(url, "_blank")
								}}
								Icon={ExternalLink}
							/>
						</Tooltip>
					</>
				)}
			</div>
		</RoleCheck>
	)
}
